/**
 * Zod schema for "Add Notification Channel" (Charter M3 migration), imported by
 * `NotificationChannelForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs `VNC-*`
 * mirror the pre-migration hand-rolled `validate()` exactly, including the two channel-type-
 * conditional required fields (`superRefine`, since they depend on a sibling field).
 */
import { z } from 'zod';
import type { CreateNotificationChannelRequest, NotificationChannelType } from '../types';
import { NOTIFICATION_CHANNEL_TYPE_OPTIONS, isParseableUrl } from './connectionFormFields';

// reason: NOTIFICATION_CHANNEL_TYPE_OPTIONS is a runtime-verified non-empty array of the exported
// union type; zod's z.enum() requires a literal non-empty tuple type.
const CHANNEL_TYPE_TUPLE = NOTIFICATION_CHANNEL_TYPE_OPTIONS.map((o) => o.value) as [
  NotificationChannelType,
  ...NotificationChannelType[],
];

function optionalTrimmed() {
  return z
    .string()
    .transform((v) => v.trim() || undefined);
}

export const NotificationChannelSchema = z
  .object({
    // VNC-01
    name: z.string().trim().min(1, 'Channel name is required'),
    channelType: z.enum(CHANNEL_TYPE_TUPLE),
    // VNC-02 (required) / VNC-03 (must be a parseable URL)
    webhookUrl: z
      .string()
      .trim()
      .min(1, 'Webhook URL is required')
      .refine(isParseableUrl, { message: 'Please enter a valid URL' }),
    webhookSecret: optionalTrimmed(),
    slackChannelId: optionalTrimmed(),
    // VNC-04 (required only when channelType === 'Slack' — see superRefine below)
    slackChannelName: optionalTrimmed(),
    teamsTeamId: optionalTrimmed(),
    teamsChannelId: optionalTrimmed(),
    // VNC-05 (required only when channelType === 'MicrosoftTeams' — see superRefine below)
    teamsChannelName: optionalTrimmed(),
  })
  .superRefine((values, ctx) => {
    // VNC-04
    if (values.channelType === 'Slack' && !values.slackChannelName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['slackChannelName'],
        message: 'Slack channel name is required',
      });
    }
    // VNC-05
    if (values.channelType === 'MicrosoftTeams' && !values.teamsChannelName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['teamsChannelName'],
        message: 'Teams channel name is required',
      });
    }
  });

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type NotificationChannelFormValues = z.input<typeof NotificationChannelSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed, undefined-if-empty). */
export type NotificationChannelFormOutput = z.output<typeof NotificationChannelSchema>;

// Compile-time proof the schema's output assembles a valid request DTO.
type _AssertAssignable = NotificationChannelFormOutput extends CreateNotificationChannelRequest ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
